/** Shared Jira / rate-limit error helpers for the frontend. */

export const JIRA_RATE_LIMIT_EVENT = 'complic:jira-rate-limit';

const JIRA_URL_HINTS = [
  '/api/tickets',
  '/api/batch',
  '/api/macros',
  '/api/quick',
  '/api/analytics',
  '/api/notifications',
];

export function isJiraApiUrl(url?: string): boolean {
  if (!url) return false;
  return JIRA_URL_HINTS.some((h) => url.includes(h));
}

export function isJiraRateLimitError(err: any): boolean {
  const status = err?.response?.status;
  if (status !== 429) return false;
  const url = String(err?.config?.url || err?.response?.config?.url || '');
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && /jira|rate limit|atlassian/i.test(detail)) return true;
  return isJiraApiUrl(url);
}

export function jiraErrorMessage(err: any, fallback = 'Nepodařilo se spojit s Jirou.'): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean);
    if (parts.length) return parts.join(', ');
  }
  if (isJiraRateLimitError(err)) {
    return 'Jira rate limit — zkuste to za chvíli znovu. Atlassian teď blokuje requesty.';
  }
  if (err?.message) return String(err.message);
  return fallback;
}

export function formatRetryRemaining(retryAtMs: number, now = Date.now()): string {
  const rem = Math.max(0, Math.ceil((retryAtMs - now) / 1000));
  if (rem <= 0) return 'okamžitě';
  const mins = Math.floor(rem / 60);
  const secs = rem % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    return `${hours} h ${mins % 60} min`;
  }
  if (mins >= 1) return `${mins} min ${secs.toString().padStart(2, '0')} s`;
  return `${secs} s`;
}

export function emitJiraRateLimit(message: string, retryAtMs?: number) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(JIRA_RATE_LIMIT_EVENT, {
      detail: { message, retryAt: retryAtMs, at: Date.now() },
    })
  );
}

/** Call from catch blocks so local UI + global banner stay in sync. */
export function notifyJiraError(err: any): string {
  const message = jiraErrorMessage(err);
  if (isJiraRateLimitError(err)) {
    const headers = err?.response?.headers || {};
    const retryAtHdr = headers['x-complic-retry-at'] || headers['X-Complic-Retry-At'];
    const ra = headers['retry-after'] || headers['Retry-After'];
    let retryAt: number | undefined;
    if (retryAtHdr) {
      const n = Number(retryAtHdr);
      if (Number.isFinite(n) && n > 1e9) retryAt = n * 1000;
    } else if (ra != null && ra !== '') {
      const sec = Number(ra);
      if (Number.isFinite(sec)) retryAt = Date.now() + sec * 1000;
    }
    emitJiraRateLimit(message, retryAt);
  }
  return message;
}
